/**
 * Asset registry — the single contract between server seed data and Phaser
 * preload. Phaser uses string `texture keys` to refer to loaded images; the
 * server uses string `asset paths` to describe what reference data points at.
 * Both sides import from this module so a rename propagates atomically.
 *
 * Path values are RELATIVE to `packages/client/public/assets/`. The client
 * preload prefixes that base; the server (when it needs to mention an asset
 * to the client) sends just the registry key, never a path.
 *
 * **MVP convention**: a single shared `player` sprite (boy_1) is used for
 * every player. Per-player visual differentiation is done at runtime via
 * `Phaser.GameObjects.Sprite.setTint()` with a deterministic hash of
 * `playerId`. No per-account avatar data is persisted.
 */
/** Phaser texture / atlas keys. Pass to `this.load.image(KEY, path)`. */
export const SPRITE_KEYS = {
    /** Single shared overworld player sprite — boy_1 walk sheet. */
    player: 'player_walk',
    creatures: {
        /** Battle-scene front-facing sprite of the opponent creature. */
        front: (speciesId) => `creature_${speciesId}_front`,
        /** Battle-scene back-facing sprite of the player's active creature. */
        back: (speciesId) => `creature_${speciesId}_back`,
    },
    tilesets: {
        /** Primary overworld tileset (terrain + water + walls). */
        general: 'tileset_general',
        /** 2-frame grass-step animation. */
        grassAnim: 'tileset_grass_anim',
    },
    maps: {
        /** Tiled JSON map key for the single MVP grass route. */
        grassZone1: 'grass-zone-1',
    },
    ui: {
        /** Battle screen HP bar chrome. */
        healthBar: 'ui_healthbar',
        /** Battle screen background panel. */
        battleBackground: 'ui_battle_bg',
    },
};
/** Disk paths under `packages/client/public/assets/`. */
export const ASSET_PATHS = {
    player: 'sprites/player_walk.png',
    creatureFront: (speciesId) => `sprites/creatures/${speciesId}_front.png`,
    creatureBack: (speciesId) => `sprites/creatures/${speciesId}_back.png`,
    tilesetGeneral: 'tilesets/general.png',
    tilesetGrassAnim: 'tilesets/grass_anim.png',
    mapGrassZone1: 'maps/grass-zone-1.tmj',
    uiHealthBar: 'sprites/ui/healthbar.png',
    uiBattleBackground: 'sprites/ui/battle_bg.png',
};
/**
 * Frame layout of `player_walk.png`. Source sheet is 144×32 with 9 frames of
 * 16×32 each; left-facing frames are produced at runtime by horizontal-flipping
 * the right-facing frames (saves 3 frames of disk).
 */
export const PLAYER_SPRITE = {
    frameWidth: 16,
    frameHeight: 32,
    frames: {
        downIdle: 0,
        downStep1: 1,
        downStep2: 2,
        upIdle: 3,
        upStep1: 4,
        upStep2: 5,
        rightIdle: 6,
        rightStep1: 7,
        rightStep2: 8,
    },
};
/** Tile size used by both the Tiled map and Phaser tilemap loader. */
export const TILE_SIZE_PX = 16;
/** Build the full preload list for a given roster of species ids. */
export function buildCreatureAssetList(speciesIds) {
    return speciesIds.flatMap((id) => [
        { key: SPRITE_KEYS.creatures.front(id), path: ASSET_PATHS.creatureFront(id) },
        { key: SPRITE_KEYS.creatures.back(id), path: ASSET_PATHS.creatureBack(id) },
    ]);
}
//# sourceMappingURL=assets.js.map